import { NotificationService } from 'src/app/shared/services/notification.service';
import { inject, Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { ActivatedRoute, Params } from '@angular/router';
import { BaseServices } from 'src/app/kuba/kuba.services';
import { ReceptionControlService } from './../services/reception-control.service';
import { ConfirmationService } from 'primeng/api';
import { FormBuilder, FormGroup } from '@angular/forms';
import { HelperService } from '../../../_services/helper.service';
import { TranslateService } from '@ngx-translate/core';
import { FeatureKey } from 'src/app/_models';
import { Table } from 'primeng/table';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Status } from 'src/app/_models';
import { UserService } from 'src/app/kuba/users/services/user.service';

@Component({
  selector: 'reception-control',
  templateUrl: 'reception-control-list.component.html',
  standalone: false
})
export class ReceptionControlListComponent implements OnInit, OnDestroy {
  private readonly notifications = inject(NotificationService);
  @ViewChild('dt', { static: false }) dt: Table;
  //#region variable
  KindOfGoods: any;
  Supplier: any;
  foodSafetyId: number;
  receptionCntrl: any[];
  parentKey: any;
  parentId: any;
  startDate: Date;
  endDate: Date;
  selectedReception: any = [];
  supplierList: any;
  goodsTypeData: any;
  ReceptionControlForm: FormGroup;
  additionalData: any;
  loading = false;
  isProductionEnabled = false;

  // Pagination variables
  currentPage: number = 1;
  pageSize: number = 10;
  totalItems: number = 0;
  totalPages: number = 0;
  isLoading: boolean = false;
  first: number = 0;

  // Filter variables
  noFilter: string = '';
  supplierNameFilter: string = '';
  kindOfGoodsFilter: string = '';
  dateFromFilter: Date | null = null;
  dateToFilter: Date | null = null;
  statusFilter: number | null = null;
  statusOptions: { label: string; value: number | null }[] = [];
  hasDeviationsFilter: boolean | null = null;

  // Sorting variables
  sortField: string = 'Default';
  sortOrder: string = 'DESC';

  // Debounce subject for filter changes
  private filterChangeSubject = new Subject<string>();

  private subscriptions: Subscription[] = [];
  //#endregion

  //#region constructor
  /**
   * constructor
   * @param receptionControlService {ReceptionControlService}
   * @param route {ActivatedRoute}
   */
  constructor(
    public receptionControlService: ReceptionControlService,
    private route: ActivatedRoute,
    private confirmationService: ConfirmationService,
    private _fb: FormBuilder,
    private translate: TranslateService,
    private userService: UserService
  ) {
    this.parentId = route.snapshot.parent.parent.params['id'];
    this.parentKey = route.snapshot.parent.parent.data['parent'];
    this.foodSafetyId = this.parentId;

    this.subscriptions.push(
      this.translate.stream('FILENAME').subscribe(val => {
        this.additionalData = {
          fileName: val.RECEPTION_CONTROL,
          header: 'Reception control',
          businessId: BaseServices.BusinessId,
          cultureInfo: BaseServices.userCultureInfo(),
          columnNames: [
            { title: 'Nr', dataKey: 'No' },
            { title: 'Supplier', dataKey: 'Supplier' },
            { title: 'Date', dataKey: 'Date' },
            { title: 'Kind of Goods', dataKey: 'KindOfGoods' },
            { title: 'Received', dataKey: 'Received' },
            { title: 'Quantity Left', dataKey: 'QtyLeft' },
            { title: 'Status', dataKey: 'StatusText' },
            { title: 'Wastage', dataKey: 'Wastage' }
          ]
        };
      })
    );
  }
  //#endregion

  /**
   * page load event
   */
  ngOnInit() {
    this.buildForm();
    this.setupFilterDebounce();
    this.setupLanguageSubscriptions();
    this.loadReceptionControls();
    this.bindReceptionTable();
    this.checkAppFeature();
  }

  buildForm() {
    this.ReceptionControlForm = this._fb.group({
      StartDate: [null],
      EndDate: [null]
    });
  }

  checkAppFeature() {
    let allAdditionalFeatures = JSON.parse(
      sessionStorage.getItem('workingModule')
    ).children;
    allAdditionalFeatures.forEach(element => {
      if (element.id === FeatureKey.PRODUCTION && element.checked == true) {
        this.isProductionEnabled = true;
      }
    });
  }

  /**
   * Load reception controls with pagination
   */
  loadReceptionControls() {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;

    const filter: any = {
      PageNumber: this.currentPage,
      PageSize: this.pageSize,
      SortColumn: this.sortField,
      SortDirection: this.sortOrder
    };

    // Only include non-null filter values
    if (this.noFilter) {
      filter.No = this.noFilter;
    }
    if (this.supplierNameFilter) {
      filter.SupplierId = this.supplierNameFilter; // Now using ID instead of name
    }
    if (this.kindOfGoodsFilter) {
      filter.KindOfGoodsId = this.kindOfGoodsFilter; // Now using ID instead of name
    }
    if (this.dateFromFilter) {
      filter.DateFrom = this.formatDateForApi(this.dateFromFilter);
    }
    if (this.dateToFilter) {
      filter.DateTo = this.formatDateForApi(this.dateToFilter);
    }
    if (this.statusFilter !== null) {
      // send numeric id to backend (Open: 8, Closed: 0)
      filter.StatusId = this.statusFilter;
    }
    if (this.hasDeviationsFilter !== null) {
      filter.HasDeviations = this.hasDeviationsFilter;
    }

    this.subscriptions.push(
      this.receptionControlService
        .getPagedReceptionControls(this.foodSafetyId, filter)
        .subscribe(
          (response: any) => {
            this.receptionCntrl = response.Items || [];
            console.log('this.receptionCntrl', this.receptionCntrl); // Transform data for frontend display
            this.receptionCntrl = this.receptionCntrl.map((item: any) => ({
              ...item,
              // Flatten nested objects for easier template access
              Supplier: item.Supplier?.Name || '',
              KindOfGoods: item.KindOfGoods?.Name || '',
              // Derive human-readable status for export/UI
              StatusText:
                parseInt(item.Status) === Status.Open
                  ? 'Open'
                  : parseInt(item.Status) === Status.Closed
                    ? 'Close'
                    : '',
              // Transform deviation list to CheckListNames for backward compatibility
              CheckListNames: item.DeviationList.map((dev: any) => ({
                id: dev.Id,
                name: dev.Name,
                status: dev.Status
              })),
              // Set OkStatus based on deviation presence
              OkStatus: item.DeviationList.length === 0,
              // Wastage is already calculated by the stored procedure
              Wastage: item.Wastage || ''
            }));

            this.totalItems = response.TotalCount || 0;
            this.totalPages = response.TotalPages || 0;
            this.isLoading = false;
          },
          error => {
            console.error('Error loading reception controls:', error);
            this.isLoading = false;
          }
        )
    );
  }

  /**
   * Setup filter debounce
   */
  private setupFilterDebounce() {
    this.subscriptions.push(
      this.filterChangeSubject
        .pipe(
          debounceTime(800) // 800ms debounce for typing
        )
        .subscribe(() => {
          this.currentPage = 1;
          this.loadReceptionControls();
        })
    );
  }

  /**
   * Setup language change subscriptions for dropdowns
   */
  private setupLanguageSubscriptions() {
    // Subscribe to language changes - call language update endpoint and translate deviation names
    this.subscriptions.push(
      this.translate.onLangChange.subscribe((event: any) => {
        // Get language ID from session storage
        const languageId = parseInt(
          sessionStorage.getItem('languageId') || '1'
        );

        // Call language update endpoint (this updates backend user language preference)
        this.userService.updateLanguage(languageId).subscribe({
          next: () => {
            // After language is updated, update deviation names in existing data
            // The translate pipe in the template will automatically handle translation
            if (this.receptionCntrl && this.receptionCntrl.length > 0) {
              this.receptionCntrl = this.receptionCntrl.map((item: any) => ({
                ...item,
                // Re-map CheckListNames to trigger change detection with new translations
                CheckListNames: item.CheckListNames
                  ? item.CheckListNames.map((dev: any) => ({
                      ...dev,
                      // Keep the same name - translate pipe will handle translation
                      name: dev.name
                    }))
                  : []
              }));
            }
          },
          error: err => {
            console.error('Error updating language:', err);
            // Still update deviation names even if endpoint call fails
            if (this.receptionCntrl && this.receptionCntrl.length > 0) {
              this.receptionCntrl = this.receptionCntrl.map((item: any) => ({
                ...item,
                CheckListNames: item.CheckListNames
                  ? item.CheckListNames.map((dev: any) => ({
                      ...dev,
                      name: dev.name
                    }))
                  : []
              }));
            }
          }
        });
      })
    );

    // Subscribe to SELECT_DROPDOWN for status options and supplier/goods type dropdowns
    this.subscriptions.push(
      this.translate.stream('SELECT_DROPDOWN').subscribe(() => {
        // Update status options
        this.updateStatusOptions();

        // Update SELECT option in supplier dropdown - create new array reference to trigger change detection
        if (this.supplierList && this.supplierList.length > 0) {
          const newSelectLabel = this.translate.instant(
            'SELECT_DROPDOWN.SELECT'
          );
          this.supplierList = [
            { label: newSelectLabel, value: null },
            ...this.supplierList.slice(1)
          ];
        }

        // Update SELECT option in goods type dropdown - create new array reference to trigger change detection
        if (this.goodsTypeData && this.goodsTypeData.length > 0) {
          const newSelectLabel = this.translate.instant(
            'SELECT_DROPDOWN.SELECT'
          );
          this.goodsTypeData = [
            { label: newSelectLabel, value: null },
            ...this.goodsTypeData.slice(1)
          ];
        }
      })
    );

    // Update statusOptions when SELECT_STATUSES changes
    this.subscriptions.push(
      this.translate.stream('SELECT_STATUSES').subscribe(() => {
        this.updateStatusOptions();
      })
    );
  }

  /**
   * Update status options from current translations
   */
  private updateStatusOptions() {
    this.statusOptions = [
      { label: this.translate.instant('SELECT_DROPDOWN.SELECT'), value: null },
      { label: this.translate.instant('SELECT_STATUSES.OPEN'), value: 8 },
      { label: this.translate.instant('SELECT_STATUSES.CLOSED'), value: 11 }
    ];
  }

  /**
   * Trigger debounced filter change
   */
  private triggerFilterChange() {
    this.filterChangeSubject.next('filter');
  }

  /**
   * Format date for API
   */
  private formatDateForApi(date: Date): string {
    return date.toISOString();
  }

  /**
   * Handle page change
   */
  onPageChange(event: any) {
    this.currentPage = event.first / event.rows + 1;
    this.pageSize = event.rows;
    this.loadReceptionControls();
  }

  /**
   * Handle sort event from table
   */
  onSort(event: any) {
    // Map frontend field names to backend field names
    let backendField = event.field;

    switch (event.field) {
      case 'No':
        backendField = 'No';
        break;
      case 'Date':
        backendField = 'Date';
        break;
      case 'Received':
        backendField = 'Received';
        break;
      case 'Supplier':
        backendField = 'SupplierName';
        break;
      case 'KindOfGoods':
        backendField = 'GoodsTypeName';
        break;
      case 'QtyLeft':
        backendField = 'QtyLeft';
        break;
      case 'Wastage':
        backendField = 'Wastage';
        break;
      default:
        backendField = 'Default';
        break;
    }

    this.sortField = backendField;
    this.sortOrder = event.order === 1 ? 'ASC' : 'DESC';
    this.loadReceptionControls();
  }

  saveReception(receptionData) {
    if (receptionData.Id > 0) {
      this.subscriptions.push(
        this.receptionControlService
          .getById(this.foodSafetyId, receptionData.Id)
          .subscribe((result: any) => {
            result.Status =
              result.Status === '8' || result.Status === 8 ? 11 : 8;
            result.BusinessId = BaseServices.BusinessId;
            this.subscriptions.push(
              this.receptionControlService
                .addReceptionControl(result)
                .subscribe(result => {
                  this.notifications.success('UI_NOTIFICATIONS.SAVED');
                  this.loadReceptionControls();
                })
            );
          })
      );
    }
  }

  onEmpChanged(e: any, empDropdown: any) {
    if (e.value) {
      this.kindOfGoodsFilter = e.value;
      this.dt.filter(empDropdown.selectedOption.label, 'KindOfGoods', 'equals');
    } else {
      this.kindOfGoodsFilter = '';
      this.dt.reset();
    }
    this.triggerFilterChange();
  }

  onSubChanged(e: any, subDropdown: any) {
    if (e.value) {
      this.supplierNameFilter = e.value;
      this.dt.filter(subDropdown.selectedOption.label, 'Supplier', 'equals');
    } else {
      this.supplierNameFilter = '';
      this.dt.reset();
    }
    this.triggerFilterChange();
  }

  onStatusChanged(e: any, statusDropdown: any) {
    if (e.value !== null) {
      this.statusFilter = e.value;
    } else {
      this.statusFilter = null;
      this.dt.reset();
    }
    this.triggerFilterChange();
  }

  bindReceptionTable() {
    this.subscriptions.push(
      this.route.parent.parent.params.subscribe((params: Params) => {
        if (params['id']) {
          this.foodSafetyId = params['id'];

          // Use resolved data instead of making API calls
          this.populateDropdownsFromResolver();
        }
      })
    );
  }

  private populateDropdownsFromResolver() {
    // Get suppliers from resolver
    const suppliers = this.route.snapshot.data['suppliers'];
    if (suppliers) {
      // Store translated value directly
      this.supplierList = [
        { label: this.translate.instant('SELECT_DROPDOWN.SELECT'), value: null }
      ];
      suppliers.forEach(element => {
        this.supplierList.push({
          label: element.Name,
          value: element.Id
        });
      });
    }

    // Get goods type from resolver
    const goodsType = this.route.snapshot.data['goodsType'];
    if (goodsType) {
      // Store translated value directly
      this.goodsTypeData = [
        { label: this.translate.instant('SELECT_DROPDOWN.SELECT'), value: null }
      ];
      goodsType.forEach(element => {
        this.goodsTypeData.push({
          label: element.Name,
          value: element.Id
        });
      });
    }
  }

  multipleDelete() {
    if (this.selectedReception.length > 0) {
      this.confirmationService.confirm({
        message: this.translate.instant('DELETE_THIS_RECORD'),
        accept: () => {
          let ids: number[] = [];
          this.selectedReception.forEach(item => {
            ids.push(item.Id);
          });
          this.subscriptions.push(
            this.receptionControlService
              .deleteReception(ids)
              .subscribe(result => {
                this.selectedReception = [];
                this.loadReceptionControls();
                this.notifications.success('UI_NOTIFICATIONS.DELETED');
              })
          );
        }
      });
    }
  }

  /**
   * Handle start date selection from PrimeNG calendar
   */
  onStartDateChanged(event: Date) {
    this.dateFromFilter = event;
    this.startDate = event;
    this.triggerFilterChange();
  }

  /**
   * Handle end date selection from PrimeNG calendar
   */
  onEndDateChanged(event: Date) {
    this.dateToFilter = event;
    this.endDate = event;
    this.triggerFilterChange();
  }

  /**
   * Handle start date clear
   */
  onStartDateCleared() {
    this.dateFromFilter = null;
    this.startDate = null;
    this.triggerFilterChange();
  }

  /**
   * Handle end date clear
   */
  onEndDateCleared() {
    this.dateToFilter = null;
    this.endDate = null;
    this.triggerFilterChange();
  }

  receptionControlDateRangeFilter(StartDate: any, EndDate: any) {
    if (StartDate != '' && EndDate != '') {
      this.subscriptions.push(
        this.receptionControlService
          .getAll(this.foodSafetyId)
          .subscribe((response: any) => {
            var filteredArr = response.filter(function (el) {
              let newStartDate = new Date(StartDate).setHours(0, 0, 0, 0);
              let newEndDate = new Date(EndDate).setHours(0, 0, 0, 0);
              let serverDate = new Date(el.Date).setHours(0, 0, 0, 0);
              return newStartDate <= serverDate && serverDate <= newEndDate;
            });
            this.receptionCntrl = filteredArr;
          })
      );
    }
  }

  /**
   * Reset all filters and sorting to default values
   */
  resetFilters() {
    // Reset filter variables
    this.noFilter = '';
    this.supplierNameFilter = '';
    this.kindOfGoodsFilter = '';
    this.dateFromFilter = null;
    this.dateToFilter = null;
    this.statusFilter = null;
    this.hasDeviationsFilter = null;

    // Reset sorting variables
    this.sortField = 'Default';
    this.sortOrder = 'DESC';

    // Reset form controls
    this.ReceptionControlForm.patchValue({
      StartDate: null,
      EndDate: null
    });

    // Reset table filters and sorting
    if (this.dt) {
      this.dt.reset();
      this.dt.sortField = '';
      this.dt.sortOrder = 0;
    }

    // Reset pagination to first page
    this.currentPage = 1;

    // Reload data
    this.loadReceptionControls();
  }

  /**
   * Get all reception controls for export with filters and sorting
   */
  getAllReceptionControlsForExport = (): Promise<any[]> => {
    const filter: any = {
      PageNumber: 1,
      PageSize: 100000, // maximum possible
      SortColumn: this.sortField,
      SortDirection: this.sortOrder
    };

    // Only include non-null filter values
    if (this.noFilter) {
      filter.No = this.noFilter;
    }
    if (this.supplierNameFilter) {
      filter.SupplierId = this.supplierNameFilter;
    }
    if (this.kindOfGoodsFilter) {
      filter.KindOfGoodsId = this.kindOfGoodsFilter;
    }
    if (this.dateFromFilter) {
      filter.DateFrom = this.formatDateForApi(this.dateFromFilter);
    }
    if (this.dateToFilter) {
      filter.DateTo = this.formatDateForApi(this.dateToFilter);
    }
    if (this.statusFilter !== null) {
      filter.StatusId = this.statusFilter;
    }
    if (this.hasDeviationsFilter !== null) {
      filter.HasDeviations = this.hasDeviationsFilter;
    }

    return new Promise((resolve, reject) => {
      this.receptionControlService
        .getPagedReceptionControls(this.foodSafetyId, filter)
        .subscribe({
          next: response => {
            const items = response.Items || [];
            // Transform data for export (mirror UI transformation + status text)
            const transformedItems = items.map((item: any) => ({
              ...item,
              Supplier: item.Supplier?.Name || '',
              KindOfGoods: item.KindOfGoods?.Name || '',
              StatusText:
                parseInt(item.Status) === Status.Open
                  ? 'Open'
                  : parseInt(item.Status) === Status.Closed
                    ? 'Close'
                    : '',
              CheckListNames: item.DeviationList.map((dev: any) => ({
                id: dev.Id,
                name: dev.Name,
                status: dev.Status
              })),
              OkStatus: item.DeviationList.length === 0,
              Wastage: item.Wastage || ''
            }));
            resolve(transformedItems);
          },
          error: err => reject(err)
        });
    });
  };

  ngOnDestroy() {
    this.subscriptions.forEach((sub, i) => {
      sub.unsubscribe();
    });
  }
}
